import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { CLIENT_TYPES, USER_TYPES } from '@medflex/utils/constants';
import {
  IconLoadBalancer,
  IconUserSolidSquare,
  IconShareAlt,
  IconBox,
  IconRefresh,
} from '@medflex/components/stories/Icons';
import { useTranslation } from 'react-i18next';
import { trackEvent } from '@medflex/utils/Tracking';
import { Link, useHistory } from 'react-router-dom';
import config from '@medflex/utils/config';
import isSelf from '@medflex/utils/isSelf';
import getFullNameWithTitle from '@medflex/utils/getFullNameWithTitle';
import { DocumentIcon } from '@heroicons/react/24/solid';
import ChatTitleBarModule from '../ChatTitleBarModule';
import isSupportPatient from '../../../utils/isSupportPatient';
import usePermissions from '../../hooks/usePermissions';
import useModal from '../../hooks/useModal';
import useChatMessagesExport from '../../hooks/useChatMessagesExport';
import useChatContacts from '../../hooks/useChatContacts';
import SubContacts from '../SubContacts';
import { MODALS } from '../../hooks/useIsModal';
import useExternalServices, { EXTERNAL_SERVICES } from '../../hooks/useExternalServices';
import useGdt from '../../hooks/useGdt';

const TitleBar = ({ contact }) => {
  const { t } = useTranslation('messenger', { keyPrefix: 'ChatTitleBarModule' });
  const { toggleArchive } = useChatContacts();
  const { open } = useModal();
  const history = useHistory();
  const chatMessagesExport = useChatMessagesExport();
  const { hasPatientAccess, hasPhysicianAccess } = usePermissions();
  const { mainContact, subContacts } = contact;
  const canCall = !isSelf(contact.user) && !mainContact;
  const { canOpenInPVS, handleOpenInPVS } = useGdt(contact.pvsId);
  const [simpleprax] = useExternalServices([EXTERNAL_SERVICES.SIMPLEPRAX]);

  const titleActionItems = useMemo(() => {
    const { mainContact } = contact;
    const isSupport = isSupportPatient({ id: contact.user.id });
    const isSubContact = typeof mainContact !== 'undefined';
    const isSelectedContactPatient = contact.type === USER_TYPES.PATIENT;
    const isSelfContact = contact && isSelf(contact.user);

    if (contact) {
      return [
        ...(USER_TYPES.PATIENT && simpleprax?.active
          ? [
              {
                text: t('startQuestionnaire'),
                onClick: () =>
                  open({ modal: MODALS.NEW_QUESTIONNAIRE, userId: contact.user.id, tab: 'groups' }),
                icon: <DocumentIcon className="w-text h-text" />,
              },
            ]
          : []),
        ...(isSupport || isSelfContact
          ? []
          : [
              {
                text: t('contact_infos'),
                onClick: event => {
                  trackEvent(event, {
                    action: 'ChatTitleBar | button',
                    actionText: 'Kontaktinfos | (audience)',
                  });
                  open({ modal: MODALS.contact_info, userId: contact.user.id });
                },
                icon: <IconUserSolidSquare size="text" className="fill-current" />,
              },
            ]),
        ...(isSelectedContactPatient && !isSupport && !isSubContact
          ? [
              {
                text: t('create_subcontact'),
                onClick: () => open({ modal: MODALS.createSubcontact, contactId: contact.id }),
                icon: <IconLoadBalancer size="text" />,
              },
            ]
          : []),
        ...([USER_TYPES.PATIENT, USER_TYPES.PHYSICIAN].includes(contact.type)
          ? [
              {
                text: t(`${contact.archivedAt ? 'dearchive' : 'archive'}`),
                onClick: async () => {
                  toggleArchive(contact);
                  if (!contact.archivedAt) {
                    history.push('/chat');
                  }
                },
                icon: contact.archivedAt ? <IconRefresh size="text" /> : <IconBox size="text" />,
              },
            ]
          : []),
        ...(isSelectedContactPatient &&
        !isSubContact &&
        hasPatientAccess &&
        hasPhysicianAccess &&
        !isSupport
          ? [
              {
                text: t('share_patient_contact'),
                onClick: event => {
                  trackEvent(event);
                  open({
                    modal: MODALS.sharePatientContact,
                    filter: 'physicians',
                    contactUserId: contact.user.id,
                  });
                },
                icon: <IconShareAlt size="text" />,
              },
            ]
          : []),
      ];
    }
    return null;
  }, [contact, hasPatientAccess, hasPhysicianAccess, history, open, t, toggleArchive]);

  return (
    config.CLIENT_TYPE !== CLIENT_TYPES.CHAT && (
      <ChatTitleBarModule
        chatMessagesExport={chatMessagesExport}
        meetingScheduleText={
          !isSelf(contact.user) && !isSupportPatient({ id: contact.user.id }) && t('call_schedule')
        }
        canCall={canCall}
        name={isSelf(contact.user) ? t('self_title') : contact.user.fullNameWithTitle}
        id={contact.user.id}
        actionItems={titleActionItems}
        canOpenInPVS={canOpenInPVS}
        handleOpenInPVS={handleOpenInPVS}
      >
        {mainContact && (
          <div className="font-normal text-sm truncate">
            {t('represented_by')}
            &nbsp;
            <Link className="text-blue-500" to={`/chat/${mainContact.id}`}>
              {getFullNameWithTitle(mainContact)}
            </Link>
          </div>
        )}

        <SubContacts items={subContacts} />
      </ChatTitleBarModule>
    )
  );
};

TitleBar.propTypes = {
  contact: PropTypes.shape({
    id: PropTypes.string,
    user: PropTypes.shape({
      id: PropTypes.string,
      fullNameWithTitle: PropTypes.string,
    }),
    archivedAt: PropTypes.string,
    type: PropTypes.string,
    mainContact: PropTypes.shape({
      id: PropTypes.string,
      firstName: PropTypes.string,
      lastName: PropTypes.string,
    }),
    subContacts: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.string,
        firstName: PropTypes.string,
        lastName: PropTypes.string,
      }),
    ),
    pvsId: PropTypes.string,
  }),
};

export default TitleBar;
